/* MenuCatalogue, now backed by the API */
import { useMemo, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronRight,
  ChevronLeft,
  Facebook,
  Instagram,
  Globe,
} from "lucide-react";
import { MenuItemCard } from "@/components/menu/MenuItemCard";
import { CategoryPicker } from "@/components/menu/CategoryPicker";
import { useCatalogue } from "@/lib/useCatalogue";

const PAGE_SIZE_MOBILE = 6;
const PAGE_SIZE_DESKTOP = 8;

function usePageSize() {
  const [size, setSize] = useState(
    typeof window !== "undefined" && window.innerWidth >= 768
      ? PAGE_SIZE_DESKTOP
      : PAGE_SIZE_MOBILE,
  );
  useEffect(() => {
    const onResize = () => {
      setSize(window.innerWidth >= 768 ? PAGE_SIZE_DESKTOP : PAGE_SIZE_MOBILE);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
}

const UI = {
  el: {
    items: "πιάτα",
    tapForAll: "πάτησε για όλες",
    next: "Επόμενο",
    prev: "Προηγούμενο",
    nextPage: "Επόμ.",
    prevPage: "Προηγ.",
    bonAppetit: "Καλή όρεξη",
    contact: "Επικοινωνία",
    address: "Διεύθυνση",
    phone: "Τηλέφωνο",
    follow: "Ακολούθησέ μας",
    priceVat: "Οι τιμές είναι σε ευρώ και περιλαμβάνουν ΦΠΑ",
    copyright: `© ${new Date().getFullYear()} Ταβέρνα Βασίλης`,
    page: "Σελίδα",
    loading: "Φόρτωση καταλόγου…",
  },
  en: {
    items: "items",
    tapForAll: "tap for all",
    next: "Next",
    prev: "Previous",
    nextPage: "Next",
    prevPage: "Prev",
    bonAppetit: "Bon appétit",
    contact: "Contact",
    address: "Address",
    phone: "Phone",
    follow: "Follow us",
    priceVat: "Prices in euro, VAT included",
    copyright: `© ${new Date().getFullYear()} Taverna Vassilis`,
    page: "Page",
    loading: "Loading menu…",
  },
};

export default function MenuCatalogue() {
  const [lang, setLang] = useState("el");
  const [groupId, setGroupId] = useState("food");
  const [pickerOpen, setPickerOpen] = useState(false);
  const PAGE_SIZE = usePageSize();
  const ui = UI[lang];

  const { data, loading } = useCatalogue();

  useEffect(() => {
    document.title = "Ταβέρνα Βασίλης — Λογγός, Παξοί | Παραδοσιακή Ελληνική Κουζίνα";
  }, []);

  const groups = data?.groups ?? [];
  const categories = data?.categories ?? [];
  const info = data?.info ?? null;

  const groupCategories = useMemo(() => {
    if (!categories.length) return [];
    return categories
      .filter((c) => c.group_id === groupId)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [categories, groupId]);

  const [activeId, setActiveId] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (groupCategories.length && !groupCategories.find((c) => c.id === activeId)) {
      setActiveId(groupCategories[0].id);
    }
  }, [groupCategories, activeId]);

  useEffect(() => {
    setPage(0);
  }, [activeId]);

  const activeCategory =
    groupCategories.find((c) => c.id === activeId) ?? groupCategories[0] ?? null;

  const totalPages = activeCategory
    ? Math.max(1, Math.ceil(activeCategory.items.length / PAGE_SIZE))
    : 1;
  const pagedItems = useMemo(() => {
    if (!activeCategory) return [];
    return activeCategory.items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  }, [activeCategory, page, PAGE_SIZE]);

  const activeIdx = groupCategories.findIndex((c) => c.id === activeId);
  const nextCat = groupCategories[activeIdx + 1];
  const prevCat = groupCategories[activeIdx - 1];
  // Numbering is per-group: «Σαλάτες» = 01, «Ορεκτικά» = 02 inside Φαγητό,
  // and «Σαμπάνια» = 01 inside Κρασιά & Ποτά.
  const categoryIndex = activeIdx >= 0 ? activeIdx + 1 : 0;

  const scrollToMenu = () => {
    const el = document.getElementById("menu-anchor");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const goToCategory = (id) => {
    setActiveId(id);
    setTimeout(scrollToMenu, 50);
  };

  const handlePageChange = (next) => {
    setPage(next);
    scrollToMenu();
  };

  if (loading || !info || !activeCategory) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="font-display text-2xl italic text-gold-muted">
          {ui.loading}
        </p>
      </div>
    );
  }

  const eyebrow = lang === "el" ? info.eyebrow_el : info.eyebrow_en;
  const title1 = lang === "el" ? info.title1_el : info.title1_en;
  const title2 = lang === "el" ? info.title2_el : info.title2_en;
  const desc = lang === "el" ? info.desc_el : info.desc_en;
  const addressL1 = lang === "el" ? info.address_el : info.address_en;
  const addressL2 = lang === "el" ? info.address_line2_el : info.address_line2_en;
  const hours = lang === "el" ? info.hours_el : info.hours_en;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="/images/hero.jpg"
            alt="Ταβέρνα Βασίλης"
            className="h-full w-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background" />
          <div className="absolute inset-0 bg-grain opacity-40" />
        </div>

        <div className="absolute right-4 top-4 z-10 sm:right-6 sm:top-6">
          <div
            data-testid="lang-toggle"
            className="flex items-center rounded-full border border-border/80 bg-card/70 p-1 backdrop-blur"
          >
            {["el", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className="relative rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest"
              >
                {lang === l && (
                  <motion.span
                    layoutId="lang-pill"
                    className="absolute inset-0 rounded-full bg-gold"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
                <span
                  className={
                    lang === l
                      ? "relative text-background"
                      : "relative text-muted-foreground"
                  }
                >
                  {l}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-5xl px-6 pb-16 pt-24 sm:pb-24 sm:pt-32">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[0.65rem] font-medium uppercase tracking-[0.32em] text-gold sm:text-xs"
          >
            {eyebrow}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 font-display text-5xl font-medium uppercase leading-[0.95] sm:text-7xl md:text-[5.5rem]"
          >
            {title1}
            <span className="block italic normal-case text-gold">{title2}</span>
          </motion.h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-6 h-px w-32 origin-left divider-gold"
          />

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base"
          >
            {desc}
          </motion.p>
        </div>
      </header>

      <div
        id="menu-anchor"
        className="sticky top-0 z-20 border-y border-border/60 bg-background/92 backdrop-blur-xl"
      >
        <div className="mx-auto max-w-5xl px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center justify-center">
            <div
              data-testid="group-switcher"
              className="relative flex w-full max-w-md items-center rounded-full border border-gold/25 bg-card/40 p-1 shadow-[0_0_0_1px_rgba(0,0,0,0.2)_inset]"
            >
              {groups.map((g) => {
                const isActive = groupId === g.id;
                return (
                  <button
                    key={g.id}
                    data-testid={`group-${g.id}`}
                    onClick={() => setGroupId(g.id)}
                    className="relative flex-1 rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors sm:text-xs"
                  >
                    {isActive && (
                      <motion.span
                        layoutId="group-pill"
                        className="absolute inset-0 rounded-full bg-gradient-to-b from-gold to-gold-muted shadow-[0_4px_18px_-4px_rgba(212,170,90,0.55)]"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      />
                    )}
                    <span
                      className={
                        isActive
                          ? "relative text-background"
                          : "relative text-muted-foreground hover:text-foreground"
                      }
                    >
                      {lang === "el" ? g.el : g.en}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mx-auto mt-3 max-w-md sm:mt-4">
            <CategoryPicker
              open={pickerOpen}
              setOpen={setPickerOpen}
              lang={lang}
              categories={groupCategories}
              activeId={activeId}
              onSelect={goToCategory}
            />
          </div>

          <div className="mt-3 flex items-center justify-center gap-1.5">
            {groupCategories.map((c, i) => (
              <button
                key={c.id}
                data-testid={`category-dot-${c.id}`}
                onClick={() => goToCategory(c.id)}
                aria-label={lang === "el" ? c.el : c.en}
                className={
                  "h-1.5 rounded-full transition-all " +
                  (i === activeIdx
                    ? "w-6 bg-gold"
                    : "w-1.5 bg-border hover:bg-gold-muted")
                }
              />
            ))}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-8 sm:px-6 sm:pt-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory.id + "-" + page}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="category-content"
          >
            <div className="mb-6 flex items-end justify-between gap-4 border-b border-border/60 pb-4 sm:mb-8 sm:pb-5">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.3em] text-gold-muted sm:text-xs">
                  {lang === "el" ? activeCategory.en : activeCategory.el}
                </p>
                <h2 className="mt-1 font-display text-3xl italic text-foreground sm:text-5xl">
                  {lang === "el" ? activeCategory.el : activeCategory.en}
                </h2>
              </div>
              <span
                aria-hidden
                className="flex items-baseline gap-1 font-display text-gold/30 sm:gap-1.5"
              >
                <span className="text-4xl sm:text-6xl">
                  {String(categoryIndex).padStart(2, "0")}
                </span>
                <span className="text-xl text-gold/20 sm:text-2xl">
                  / {String(groupCategories.length).padStart(2, "0")}
                </span>
              </span>
            </div>

            <div className="space-y-0">
              {pagedItems.map((item, i) => (
                <MenuItemCard
                  key={`${activeCategory.id}-${page}-${i}-${item.id}`}
                  item={item}
                  lang={lang}
                  index={i}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {totalPages > 1 && (
          <div
            data-testid="pagination"
            className="mt-10 flex items-center justify-between gap-4 border-t border-border/40 pt-6"
          >
            <button
              data-testid="pagination-prev"
              onClick={() => handlePageChange(Math.max(0, page - 1))}
              disabled={page === 0}
              aria-label="Previous page"
              className="flex items-center gap-1.5 rounded-full border border-border/80 bg-card/50 px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground transition-colors hover:text-gold disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{ui.prevPage}</span>
            </button>

            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  data-testid={`pagination-dot-${i}`}
                  onClick={() => handlePageChange(i)}
                  aria-label={`${ui.page} ${i + 1}`}
                  className={
                    "h-1.5 rounded-full transition-all " +
                    (i === page
                      ? "w-6 bg-gold"
                      : "w-1.5 bg-border hover:bg-gold-muted")
                  }
                />
              ))}
            </div>

            <button
              data-testid="pagination-next"
              onClick={() => handlePageChange(Math.min(totalPages - 1, page + 1))}
              disabled={page === totalPages - 1}
              aria-label="Next page"
              className="flex items-center gap-1.5 rounded-full border border-border/80 bg-card/50 px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground transition-colors hover:text-gold disabled:cursor-not-allowed disabled:opacity-30"
            >
              <span className="hidden sm:inline">{ui.nextPage}</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 gap-3 sm:mt-14 sm:grid-cols-2 sm:gap-4">
          {prevCat ? (
            <button
              data-testid="prev-category"
              onClick={() => goToCategory(prevCat.id)}
              className="group flex flex-col items-start rounded-2xl border border-border/60 bg-card/40 p-5 text-left transition-all hover:border-gold/40 hover:bg-card/70 sm:p-6"
            >
              <span className="flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.3em] text-gold-muted sm:text-xs">
                <ChevronLeft className="h-3.5 w-3.5" />
                {ui.prev}
              </span>
              <span className="mt-2 font-display text-2xl italic text-foreground transition-colors group-hover:text-gold sm:text-3xl">
                {lang === "el" ? prevCat.el : prevCat.en}
              </span>
            </button>
          ) : (
            <span aria-hidden className="hidden sm:block" />
          )}
          {nextCat && (
            <button
              data-testid="next-category"
              onClick={() => goToCategory(nextCat.id)}
              className={
                "group flex flex-col items-end rounded-2xl border border-border/60 bg-card/40 p-5 text-right transition-all hover:border-gold/40 hover:bg-card/70 sm:p-6 " +
                (!prevCat ? "sm:col-start-2" : "")
              }
            >
              <span className="flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.3em] text-gold-muted sm:text-xs">
                {ui.next}
                <ChevronRight className="h-3.5 w-3.5" />
              </span>
              <span className="mt-2 font-display text-2xl italic text-foreground transition-colors group-hover:text-gold sm:text-3xl">
                {lang === "el" ? nextCat.el : nextCat.en}
              </span>
            </button>
          )}
        </div>
      </main>

      <footer className="border-t border-border/60 px-6 py-12 sm:py-14">
        <div className="mx-auto max-w-5xl">
          <div className="mx-auto h-px w-24 divider-gold" />
          <p className="mt-6 text-center font-display text-3xl italic text-foreground sm:text-4xl">
            {ui.bonAppetit}
          </p>

          <p className="mt-10 text-center text-[0.65rem] uppercase tracking-[0.3em] text-gold sm:text-xs">
            {ui.contact}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-12">
            <div className="text-center sm:text-left">
              <p className="text-[0.6rem] uppercase tracking-[0.3em] text-gold-muted sm:text-[0.7rem]">
                {ui.address}
              </p>
              <p className="mt-2 font-display text-2xl italic text-foreground sm:text-3xl">
                {addressL1}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{addressL2}</p>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-[0.6rem] uppercase tracking-[0.3em] text-gold-muted sm:text-[0.7rem]">
                {ui.phone}
              </p>
              <a
                href={`tel:${(info.phone || "").replace(/\s/g, "")}`}
                data-testid="phone-link"
                className="mt-2 inline-block font-display text-2xl italic text-foreground transition-colors hover:text-gold sm:text-3xl"
              >
                {info.phone}
              </a>
              <p className="mt-1 text-sm text-muted-foreground">{hours}</p>
            </div>
          </div>

          <div className="mt-10 flex flex-col items-center gap-4">
            <p className="text-[0.6rem] uppercase tracking-[0.3em] text-gold-muted sm:text-[0.7rem]">
              {ui.follow}
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: Facebook, href: info.facebook_url || "#", label: "Facebook" },
                { icon: Instagram, href: info.instagram_url || "#", label: "Instagram" },
                { icon: Globe, href: info.website_url || "#", label: "Website" },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  href={href}
                  target={href !== "#" ? "_blank" : undefined}
                  rel={href !== "#" ? "noopener noreferrer" : undefined}
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-card/40 text-muted-foreground transition-all hover:border-gold/50 hover:text-gold"
                >
                  <Icon className="h-4 w-4" strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>

          <div className="mt-12 border-t border-border/40 pt-6 text-center">
            <p className="text-[0.6rem] uppercase tracking-[0.28em] text-muted-foreground sm:text-[0.7rem]">
              {ui.priceVat}
            </p>
            <p className="mt-2 text-[0.6rem] uppercase tracking-[0.28em] text-muted-foreground/70 sm:text-[0.7rem]">
              {ui.copyright}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
